/*
  load-more.js
  ------------
  Progressive-enhancement pagination for main-collection-product-grid.liquid.
  The rendered <a> always has a real next-page href (crawlable, works with
  JS off); this script intercepts the click and fetches/appends the next
  page's products in place instead of a full reload. Ported from the same
  pattern already proven across the Legends theme portfolio (see e.g.
  shopify-theme-fifa-official/assets/loadmore.js) rather than invented fresh.
*/
(function () {
  const gridSelector = '#product-grid';
  const shownCountSelector = '.js-collection-length';
  const loadMoreBtnSelector = '.js-load-more';

  let isLoading = false;

  /* ---------------------------
     ARIA live region (announces progress to screen readers)
  ---------------------------- */
  function getLiveRegion() {
    let live = document.getElementById('load-more-live');
    if (!live) {
      live = document.createElement('div');
      live.id = 'load-more-live';
      live.className = 'visually-hidden';
      live.setAttribute('aria-live', 'polite');
      live.setAttribute('aria-atomic', 'true');
      document.body.appendChild(live);
    }
    return live;
  }

  function announce(msg) {
    getLiveRegion().textContent = msg;
  }

  /* ---------------------------
     Helpers
  ---------------------------- */
  function getGrid() {
    return document.querySelector(gridSelector);
  }

  function getLoadMoreBtn() {
    return document.querySelector(loadMoreBtnSelector);
  }

  function updateShownCount() {
    const grid = getGrid();
    const countEl = document.querySelector(shownCountSelector);
    if (!grid || !countEl) return;
    countEl.value = grid.querySelectorAll('li.grid__item').length;
  }

  function updateProductCountText() {
    const grid = getGrid();
    const span = document.querySelector('.js-collection-count-text');
    if (!grid || !span) return;
    const count = grid.querySelectorAll('li.grid__item').length;
    if (span.textContent != count) span.textContent = count;
  }

  function updateProductAriaLabels() {
    const items = document.querySelectorAll(`${gridSelector} li.grid__item`);
    const total = items.length;
    items.forEach((item, index) => {
      item.removeAttribute('aria-label');
      const wrapper = item.querySelector('.card-wrapper');
      if (wrapper) {
        wrapper.setAttribute('role', 'group');
        wrapper.setAttribute('aria-label', `Product ${index + 1} of ${total}`);
      }
    });
  }

  function setButtonState(btn, loading) {
    if (!btn) return;
    const textSpan = btn.querySelector('span');
    if (loading) {
      btn.setAttribute('aria-disabled', 'true');
      btn.setAttribute('aria-busy', 'true');
      btn.dataset.originalText = textSpan.textContent;
      textSpan.textContent = 'Loading…';
    } else {
      btn.removeAttribute('aria-busy');
      btn.setAttribute('aria-disabled', 'false');
      textSpan.textContent = btn.dataset.originalText || textSpan.textContent;
    }
  }

  function hideButton(btn) {
    const wrap = btn.closest('.load-more_wrap');
    if (wrap) wrap.style.display = 'none';
  }

  /* ---------------------------
     Build the fetch URL and read the next link from the response
  ---------------------------- */
  // Section id of the PLP grid, so we can ask Shopify for just that section's
  // HTML (Section Rendering API) instead of the whole page.
  function getSectionId() {
    const wrapper = document.getElementById('ProductGridContainer')?.closest('[id^="shopify-section-"]');
    return wrapper ? wrapper.id.replace('shopify-section-', '') : '';
  }

  // The crawlable href, plus any active filter/sort params the page URL has
  // that it doesn't already carry, plus section_id for a slim response.
  function buildFetchUrl(nextUrl) {
    const url = new URL(nextUrl, window.location.origin);
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (key !== 'page' && key !== 'section_id' && !url.searchParams.has(key, value)) {
        url.searchParams.append(key, value);
      }
    });
    const sectionId = getSectionId();
    if (sectionId) url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  // Same as the Kansas (KU) theme: the next link is the fetched page's own
  // rendered load-more button, i.e. paginate.next.url, not a <link rel="next">
  // (which this theme does not output).
  function parseHTML(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newItems = doc.querySelectorAll(`${gridSelector} li.grid__item`);
    const nextBtn = doc.querySelector(loadMoreBtnSelector);
    let nextUrl = '';
    if (nextBtn) {
      // Keep section_id out of the href so it stays a clean, crawlable page URL.
      const next = new URL(nextBtn.getAttribute('href'), window.location.origin);
      next.searchParams.delete('section_id');
      nextUrl = next.pathname + next.search;
    }
    return { newItems, nextUrl };
  }

  /* ---------------------------
     Main entry point (called from the rendered onclick attribute)
  ---------------------------- */
  window.loadMoreProducts = async function (nextUrl, event) {
    event.preventDefault();
    if (isLoading) return;

    const grid = getGrid();
    const btn = getLoadMoreBtn();
    if (!grid || !btn) return;

    isLoading = true;
    setButtonState(btn, true);
    announce('Loading more products…');
    document.getElementById('ProductGridContainer')?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(buildFetchUrl(nextUrl));
      if (!response.ok) throw new Error(`Load more failed: ${response.status}`);
      const html = await response.text();
      const beforeCount = grid.children.length;
      const { newItems, nextUrl: newNextUrl } = parseHTML(html);

      if (!newItems.length) {
        hideButton(btn);
        announce('All products loaded.');
        return;
      }

      newItems.forEach((item) => grid.appendChild(item));
      updateShownCount();
      updateProductCountText();
      updateProductAriaLabels();

      const afterCount = grid.children.length;
      announce(`${afterCount - beforeCount} more products loaded. Now showing ${afterCount}.`);

      if (newNextUrl) {
        btn.setAttribute('href', newNextUrl);
        btn.onclick = (e) => {
          e.preventDefault();
          window.loadMoreProducts(newNextUrl, e);
        };
      } else {
        hideButton(btn);
      }

      const firstNew = newItems[0];
      firstNew.setAttribute('tabindex', '-1');
      firstNew.focus();
      firstNew.removeAttribute('tabindex');
    } catch (e) {
      console.error(e);
      announce('Error loading products.');
    } finally {
      isLoading = false;
      setButtonState(btn, false);
      document.getElementById('ProductGridContainer')?.removeAttribute('aria-busy');
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    updateShownCount();
    updateProductCountText();
    updateProductAriaLabels();
  });

  // facets.js replaces ProductGridContainer's innerHTML on every filter/sort
  // change (and no longer dispatches a facet event), which resets the
  // "Showing X of Y" number to empty. Watch the container and refill it.
  function watchGridContainer() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    new MutationObserver(() => {
      updateShownCount();
      updateProductCountText();
      updateProductAriaLabels();
    }).observe(container, { childList: true });
  }

  document.addEventListener('DOMContentLoaded', watchGridContainer);
})();
